import type { SyntheticDataBatchRequest } from "services/SyntheticDataService";
import type { DataStepRubricItem } from "../../Stores/LLMClassifierBuilderStore";
import type { SyntheticDataRow } from "./syntheticDataRows";

export type SyntheticDataBatchPlan =
    | {
          canGenerate: true;
          requests: SyntheticDataBatchRequest[];
          /** Each result's answer tag id: the answer its description came from. */
          tagIds: Map<string, string>;
      }
    | { canGenerate: false; reason: string };

/**
 * Builds one batch request per result that has synthetic calls to write.
 * Every request carries every result in its taxonomy, including ones with no labels.
 */
export function planSyntheticDataBatches(options: {
    questionId: string;
    labelName: string;
    rows: SyntheticDataRow[];
    rubric: DataStepRubricItem[];
    // The model version the batch was generated from.
    labelRubricVersion: string;
    batchIdFor: (result: string) => string;
}): SyntheticDataBatchPlan {
    // Answers that share the same text are one row, so the first described answer wins.
    const described = new Map<string, { id: string; definition: string }>();
    for (const item of options.rubric) {
        const result = item.result.trim();
        const definition = item.definition.trim();
        if (definition && !described.has(result))
            described.set(result, { id: item.id, definition });
    }

    const taxonomy = options.rows.map((row) => ({
        value: row.result,
        definition: described.get(row.result.trim())?.definition ?? "",
    }));
    const undescribed = taxonomy
        .filter((item) => !item.definition)
        .map((item) => item.value);
    if (undescribed.length > 0) {
        return {
            canGenerate: false,
            reason: `Add a description for ${undescribed.join(
                ", ",
            )} in the Rubric.`,
        };
    }
    if (taxonomy.length < 2) {
        return {
            canGenerate: false,
            reason: "The question needs at least two answers to generate synthetic calls.",
        };
    }

    const answers = taxonomy
        .map((item) => `- ${item.value}: ${item.definition}`)
        .join("\n");
    const labelRubric = `Decide which answer the conversation supports for the question "${options.labelName}". Read the whole conversation, then choose the one answer whose definition fits best.\n\n${answers}`;

    const requests = options.rows
        .filter((row) => row.syntheticCalls > 0)
        .map((row) => ({
            batchId: options.batchIdFor(row.result),
            questionId: options.questionId,
            labelName: options.labelName,
            labelValue: row.result,
            labelRubric,
            labelRubricVersion: options.labelRubricVersion,
            labelTaxonomy: taxonomy,
            count: row.syntheticCalls,
            // Enough synthetic calls per real call to reach the count.
            maxUpsamplingRatio: Math.ceil(row.syntheticCalls / row.humanLabels),
            deliberateHardExamples: false,
            instructions: null,
            sourceAudioMetadataIds: [],
        }));
    if (requests.length === 0) {
        return {
            canGenerate: false,
            reason: "Add synthetic calls for at least one result.",
        };
    }
    const tagIds = new Map(
        requests.map((request) => [
            request.labelValue,
            described.get(request.labelValue.trim())!.id,
        ]),
    );
    return { canGenerate: true, requests, tagIds };
}
